import { DOCUMENT_TYPE, getInsuranceTypeName, getInsuranceTypeFromTabName } from '../utils/insuranceTypeUtils';
import DocumentTypeSelect from './DocumentTypeSelect';
import InsuranceCompanySelect from './InsuranceCompanySelect';
import ProductTypeSelect from './ProductTypeSelect';
import DateRangePicker from './DateRangePicker';
import SearchAutocomplete from './SearchAutocomplete';
import { isDateRangeApplicable } from '../utils/documentTypeUtils';

const SearchSidebar = ({
  isMobile,
  sidebarMinimized,
  sidebarActive,
  toggleSidebarMinimized,
  documentType,
  setDocumentType,
  documentTypes = [],
  selectedInsuranceCompanies,
  setSelectedInsuranceCompanies,
  selectedProductTypes,
  setSelectedProductTypes,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  searchQuery,
  setSearchQuery,
  onSearch,
  onReset,
  insuranceCompanies,
  isLoading,
  searchExecutionSignal = 0,
  activeTab = 'life',
  onTabChange,
  isSearchDisabled = false,
}) => {
  const onDocumentTypeChange = (documentTypeCode) => {
    // 引受は保険会社指定不要のため選択をクリア
    if (documentTypeCode === DOCUMENT_TYPE.HIKIUKE) {
      setSelectedInsuranceCompanies([]);
    }
    setDocumentType(documentTypeCode);
  };

  const placeholder =
    activeTab === 'life' && documentType === DOCUMENT_TYPE.HIKIUKE
      ? '例：心筋梗塞'
      : activeTab === 'life'
      ? '例：がん 診断 2回目'
      : '例：自動車 事故 対人';

  return (
    <aside className={`insurance-sidebar ${isMobile ? 'mobile' : ''} ${sidebarMinimized ? 'minimized' : ''} ${sidebarActive ? 'active' : ''}`}>
      {!sidebarMinimized ? (
        <>
          {/* PC sidebar toggle button */}
          {!isMobile && (
            <div className="sidebar-toggle-pc">
              <button className="sidebar-toggle-button-pc select-box ver02" onClick={toggleSidebarMinimized}>
                <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 2H10.5" stroke="#0073BC" />
                  <path d="M13.5 2H14.5" stroke="#0073BC" />
                  <circle cx="12" cy="2" r="1.5" stroke="#0073BC" />
                  <path d="M0 12L8.5 12" stroke="#0073BC" />
                  <path d="M11.5 12L14.5 12" stroke="#0073BC" />
                  <circle cx="10" cy="12" r="1.5" stroke="#0073BC" />
                  <path d="M0 7L4 7" stroke="#0073BC" />
                  <path d="M6.5 7H14.5" stroke="#0073BC" />
                  <circle cx="5" cy="7" r="1.5" stroke="#0073BC" />
                </svg>
                <span>閉じる</span>
              </button>
            </div>
          )}

          <div className="sidebar-header">
            検索条件
          </div>

          <div className={`sidebar-section ${isMobile ? 'mobile-safe-area' : ''}`}>
            <div className="document-section">
              <div className='document-tabs-section'>
                <div className="search-tabs">
                  <button className={`tab-button-search ${activeTab === 'life' ? 'active' : ''}`} onClick={() => onTabChange('life')}>{getInsuranceTypeName(1)}</button>
                  <button className={`tab-button-search ${activeTab === 'sonpo' ? 'active' : ''}`} onClick={() => onTabChange('sonpo')}>{getInsuranceTypeName(2)}</button>
                </div>
                <div className="section-note">「※」の項目選択を必須とします。</div>
              </div>

              {/* Document Type Selection */}
              <DocumentTypeSelect
                documentType={documentType}
                setDocumentType={onDocumentTypeChange}
                insuranceType={getInsuranceTypeFromTabName(activeTab)}
                documentTypes={documentTypes}
              />

              {/* Insurance Company Selection */}
              <InsuranceCompanySelect
                selectedInsuranceCompanies={selectedInsuranceCompanies}
                setSelectedInsuranceCompanies={setSelectedInsuranceCompanies}
                insuranceCompanies={insuranceCompanies}
                insuranceType={getInsuranceTypeFromTabName(activeTab)}
                documentType={documentType}
              />

              {/* Product Type Selection */}
              {documentType !== DOCUMENT_TYPE.HIKIUKE && (
                <ProductTypeSelect
                  selectedProductTypes={selectedProductTypes}
                  setSelectedProductTypes={setSelectedProductTypes}
                  insuranceType={getInsuranceTypeFromTabName(activeTab)}
                />
              )}

              {/* Date Range Selection - しおり・約款、または全文書種別の場合 */}
              {isDateRangeApplicable(documentType) && (
                <DateRangePicker
                  dateFrom={dateFrom}
                  setDateFrom={setDateFrom}
                  dateTo={dateTo}
                  setDateTo={setDateTo}
                />
              )}

              {/* Word Search */}
              <div className="word-search">
                <div className="option-label">
                  {documentType === DOCUMENT_TYPE.HIKIUKE ? <p>傷病名検索</p> : <p>ワード検索</p>}
                  <span className="required-mark required-mark-red">*</span>
                </div>
                <SearchAutocomplete
                  searchQuery={searchQuery}
                  documentType={documentType}
                  activeTab={activeTab}
                  isSearchDisabled={isSearchDisabled}
                  isLoading={isLoading}
                  searchExecutionSignal={searchExecutionSignal}
                  placeholder={placeholder}
                  onQueryChange={setSearchQuery}
                  onSearch={onSearch}
                />
              </div>

              {/* Search and Reset Buttons */}
              <div className={`button-container ${isMobile ? 'mobile-buttons' : ''}`}>
                <button
                  className={`search-btn ${isLoading ? 'loading' : ''}`}
                  onClick={onSearch}
                  disabled={isLoading || isSearchDisabled}
                >
                  {isLoading ? '検索中...' : '検索'}
                </button>

                <button className="reset-btn" onClick={onReset}>
                  条件をリセットする
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        // Minimized sidebar display
        <div className="minimized-sidebar-content">
          <button className="sidebar-toggle-button-pc select-box ver03" onClick={toggleSidebarMinimized}>
            <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 2H10.5" stroke="#0073BC" />
              <path d="M13.5 2H14.5" stroke="#0073BC" />
              <circle cx="12" cy="2" r="1.5" stroke="#0073BC" />
              <path d="M0 12L8.5 12" stroke="#0073BC" />
              <path d="M11.5 12L14.5 12" stroke="#0073BC" />
              <circle cx="10" cy="12" r="1.5" stroke="#0073BC" />
              <path d="M0 7L4 7" stroke="#0073BC" />
              <path d="M6.5 7H14.5" stroke="#0073BC" />
              <circle cx="5" cy="7" r="1.5" stroke="#0073BC" />
            </svg>
            <span>開く</span>
          </button>
        </div>
      )}
    </aside>
  );
};

export default SearchSidebar;
